import type { Bundle, Device, Parameters, Questionnaire, QuestionnaireResponse } from "fhir/r5";
import Client from "fhir-kit-client";
import { addRequiredCategory, type DeviceKind } from "./DeviceAdapter";

type FhirClient = Pick<Client, "read" | "create">;
type OperationClient = Pick<Client, "operation">;

export function createQuestionnaireService(
  fhirClient: FhirClient,
  operationClient: OperationClient,
) {
  async function loadQuestionnaire(questionnaireId: string): Promise<Questionnaire> {
    return fhirClient.read({
      resourceType: "Questionnaire",
      id: questionnaireId,
    }) as Promise<Questionnaire>;
  }

  async function populate(
    questionnaire: Questionnaire,
    subjectId: string,
  ): Promise<QuestionnaireResponse> {
    const input: Parameters = {
      resourceType: "Parameters",
      parameter: [
        { name: "questionnaire", resource: questionnaire },
        { name: "subject", valueString: subjectId },
      ],
    };
    return operationClient.operation({
      name: "populate",
      input,
      resourceType: "Questionnaire",
    }) as Promise<QuestionnaireResponse>;
  }

  async function extract(
    questionnaireId: string,
    questionnaireResponse: QuestionnaireResponse,
  ): Promise<Bundle> {
    // The operation is type-level; keep the historical argument for callers.
    void questionnaireId;
    const input: Parameters = {
      resourceType: "Parameters",
      parameter: [{ name: "questionnaire-response", resource: questionnaireResponse }],
    };
    return operationClient.operation({
      name: "extract",
      input,
      resourceType: "QuestionnaireResponse",
    }) as Promise<Bundle>;
  }

  async function submitDevice(
    response: QuestionnaireResponse,
    kind: DeviceKind,
  ): Promise<Device[]> {
    const responseWithoutSubject = { ...response };
    delete responseWithoutSubject.subject;

    const questionnaireId = kind === "software" ? "Questionnaire-Software" : "Questionnaire-Material";
    const extracted = await extract(questionnaireId, responseWithoutSubject);
    const devices = extracted.entry
      ?.map((entry) => entry.resource)
      .filter((resource): resource is Device => resource?.resourceType === "Device") ?? [];

    if (devices.length === 0) {
      throw new Error("The questionnaire extraction returned no Device resource.");
    }

    await fhirClient.create({
      resourceType: "QuestionnaireResponse",
      body: responseWithoutSubject,
    });

    const createdDevices: Device[] = [];
    for (const device of devices) {
      const created = await fhirClient.create({
        resourceType: "Device",
        body: addRequiredCategory(device, kind) as Device,
      });
      createdDevices.push(created as Device);
    }
    return createdDevices;
  }

  return { loadQuestionnaire, populate, extract, submitDevice };
}

const QuestionnaireService = createQuestionnaireService(
  new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" }),
  new Client({ baseUrl: process.env.REACT_APP_QUESTIONNAIRE_URL ?? process.env.REACT_APP_FHIR_URL ?? "fhir" }),
);

export default QuestionnaireService;
